import DashboardQuickActions from "../components/DashboardQuickActions";
import { Text, TextLinear } from "../../../core";
import { useAppSelector } from "@packages/store/hooks";
import DashboardMainOnboarding from "../components/DashboardMainOnboarding";
// import DailyInfoModal, {
//   useDailyInfoModal,
// } from "../components/DailyInfoModal";
import {
  useGetTeacherMinuteSaved,
  useGetTeachersMinuteSaved,
} from "@packages/api/madlenAPI";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const DashboardPageView = () => {
  const teacherName = useAppSelector((state) => state.user.teacher?.name);
  const { t, i18n } = useTranslation();
  // const { isModalOpen, closeModal } = useDailyInfoModal();

  const { data: teacherMinuteSaved } = useGetTeacherMinuteSaved();
  const { data: globalMinuteSaved } = useGetTeachersMinuteSaved();

  const formatMinutesToTimeString = (minutes: number | undefined) => {
    if (!minutes || minutes === 0) return undefined;

    if (minutes < 60) {
      const formattedMinutes = minutes.toLocaleString("tr-TR");
      return `${formattedMinutes} ${t("common.time.minutes")}`;
    }

    const hours = Math.ceil(minutes / 60);
    // Saatleri nokta ile formatla
    const formattedHours = hours.toLocaleString("tr-TR");
    return `${formattedHours} ${t("common.time.hours")}`;
  };

  const formattedTeacherTimeSaved = useMemo(
    () => formatMinutesToTimeString(teacherMinuteSaved?.minutesSaved),
    [teacherMinuteSaved],
  );

  const formattedGlobalTimeSaved = useMemo(
    () => formatMinutesToTimeString(globalMinuteSaved?.minutesSaved),
    [globalMinuteSaved],
  );

  const hasTeacherMinutes =
    teacherMinuteSaved?.minutesSaved !== undefined &&
    teacherMinuteSaved.minutesSaved > 0;

  const hasGlobalMinutes =
    globalMinuteSaved?.minutesSaved !== undefined &&
    globalMinuteSaved.minutesSaved > 0;

  return (
    <div className="flex-1 p-4 bg-gray-50 dark:bg-gray-900">
      <DashboardMainOnboarding />
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex flex-col gap-2 items-start">
          <Text variant="h1" values={{ name: teacherName }}>
            welcome
          </Text>
          <TextLinear
            variant="h3"
            from="from-purple-600 dark:from-purple-400"
            via="via-fuchsia-600 dark:via-fuchsia-400"
            to="to-pink-600 dark:to-pink-400"
          >
            dashboard.madlen_time_saver
          </TextLinear>
        </div>

        {(hasTeacherMinutes || hasGlobalMinutes) && (
          <div className="mb-8">
            <div className="relative overflow-hidden bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 dark:from-indigo-950/40 dark:via-purple-950/30 dark:to-blue-950/40 rounded-2xl p-5 shadow-xl border border-indigo-200/40 dark:border-indigo-700/30 backdrop-blur-sm hover:shadow-2xl transition-all duration-300">
              {/* Decorative background elements */}
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-gradient-to-br from-pink-300/20 to-purple-300/20 dark:from-pink-500/10 dark:to-purple-500/10 rounded-full blur-xl"></div>
              <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-gradient-to-tr from-blue-300/20 to-teal-300/20 dark:from-blue-500/10 dark:to-teal-500/10 rounded-full blur-xl"></div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-tr from-indigo-300/10 to-violet-300/10 dark:from-indigo-500/5 dark:to-violet-500/5 rounded-full blur-3xl"></div>

              {/* Main content - Unified layout for mobile and desktop */}
              <div className="relative flex w-full flex-col items-center">
                {/* Main flex container - column on mobile, row on desktop */}
                <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-8 sm:gap-4 py-6">
                  {/* Teacher time saved - left on desktop, top on mobile */}
                  {hasTeacherMinutes ? (
                    <div className="flex flex-col sm:flex-row items-center sm:items-center sm:space-x-2 text-center sm:text-left w-full sm:flex-1 order-1">
                      <Text
                        variant="h3"
                        className="text-balance text-base"
                        values={{
                          time: formattedTeacherTimeSaved?.toLowerCase(),
                        }}
                      >
                        {i18n.language === "tr"
                          ? "Bugüne kadar"
                          : i18n.language === "en"
                            ? "Today you saved"
                            : i18n.language === "de"
                              ? "Du hast heute gespart"
                              : i18n.language === "fr"
                                ? "Aujourd'hui vous avez économisé"
                                : i18n.language === "es"
                                  ? "Hoy has ahorrado"
                                  : "Today you saved"}
                      </Text>
                      <TextLinear
                        variant="h2"
                        className="text-xl font-bold border-b border-purple-500"
                        from="from-indigo-600 dark:from-indigo-400"
                        via="via-purple-600 dark:via-purple-400"
                        to="to-pink-600 dark:to-pink-400"
                      >
                        {formattedTeacherTimeSaved}
                      </TextLinear>
                      <Text
                        variant="h3"
                        className="text-balance text-base mt-1"
                        values={{
                          time: formattedTeacherTimeSaved?.toLowerCase(),
                        }}
                      >
                        {i18n.language === "tr"
                          ? "tasarruf edildi!"
                          : "with Madlen"}
                      </Text>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center sm:items-start text-center sm:text-left w-full sm:flex-1 order-1">
                      <Text variant="h3" className="text-balance text-base">
                        dashboard.no_time_saved
                      </Text>
                    </div>
                  )}

                  {/* Logo in the center - middle position on both layouts */}
                  <div className="relative w-24 h-24 order-2 flex-shrink-0 flex justify-center">
                    {/* Outer ring - global time */}
                    {hasGlobalMinutes && (
                      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600/20 via-teal-600/20 to-emerald-600/20 dark:from-blue-400/10 dark:via-teal-400/10 dark:to-emerald-400/10 animate-pulse"></div>
                    )}

                    <div className="absolute inset-3 rounded-full bg-gradient-to-r from-indigo-600/30 via-purple-600/30 to-pink-600/30 dark:from-indigo-400/15 dark:via-purple-400/15 dark:to-pink-400/15 animate-pulse animation-delay-700"></div>

                    {/* Logo in the center of circles */}
                    <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                      <div className="w-12 h-12 rounded-full bg-white/80 dark:bg-gray-800/90 shadow-lg flex items-center justify-center">
                        <img
                          src="/logo.svg"
                          alt="Madlen Logo"
                          className="w-8 h-8 object-contain animate-pulse"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = "/favicon.ico";
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Global time saved - right on desktop, bottom on mobile */}
                  {hasGlobalMinutes && (
                    <div className="flex flex-col items-center sm:items-end text-center sm:text-right w-full sm:flex-1 order-3">
                      <TextLinear
                        variant="h2"
                        className="text-xl font-bold border-b border-blue-500"
                        from="from-blue-600 dark:from-blue-400"
                        via="via-teal-600 dark:via-teal-400"
                        to="to-emerald-600 dark:to-emerald-400"
                      >
                        {formattedGlobalTimeSaved}
                      </TextLinear>
                      <Text
                        variant="h3"
                        className="text-balance text-xs mt-1"
                        values={{
                          time: formattedGlobalTimeSaved?.toLowerCase(),
                        }}
                      >
                        {i18n.language === "tr"
                          ? "Şimdiye kadar kazanılan toplam öğretmen zamanı"
                          : i18n.language === "en"
                            ? "Total time saved for teachers worldwide"
                            : i18n.language === "de"
                              ? "Gesamte Zeitersparnis für Lehrer weltweit"
                              : i18n.language === "fr"
                                ? "Temps total gagné pour les enseignants du monde entier"
                                : i18n.language === "es"
                                  ? "Tiempo total ahorrado para los maestros de todo el mundo"
                                  : "Total time saved for teachers worldwide"}
                      </Text>
                    </div>
                  )}
                </div>

                {/* Animated particles */}
                <div className="absolute top-0 right-0 w-2 h-2 bg-indigo-400 rounded-full animate-ping"></div>
                <div className="absolute bottom-0 left-0 w-2 h-2 bg-emerald-400 rounded-full animate-ping animation-delay-1000"></div>
                <div className="absolute top-1/2 left-0 w-1.5 h-1.5 bg-purple-400 rounded-full animate-ping animation-delay-1500"></div>
                <div className="absolute bottom-1/2 right-0 w-1.5 h-1.5 bg-blue-400 rounded-full animate-ping animation-delay-500"></div>
              </div>

              {/* Additional animated elements */}
              <div className="absolute top-1/4 left-1/4 w-1.5 h-1.5 bg-yellow-300 rounded-full animate-pulse"></div>
              <div className="absolute top-3/4 right-1/4 w-1 h-1 bg-purple-400 rounded-full animate-ping animation-delay-700"></div>
              <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-blue-400 rounded-full animate-ping animation-delay-1500"></div>
              <div className="absolute bottom-1/3 left-1/3 w-0.5 h-0.5 bg-pink-400 rounded-full animate-ping animation-delay-2000"></div>
            </div>
          </div>
        )}

        <DashboardQuickActions />
      </div>

      {/* Daily Info Modal */}
      {/* <DailyInfoModal isOpen={isModalOpen} onClose={closeModal} /> */}
    </div>
  );
};

export default DashboardPageView;
